import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api';
import { useLanguage } from '../LanguageContext';
import './TaskList.css';

const DOMAINS = ['全部','软件工程','人工智能','计算机学','电子信息','通信工程','大数据','理论研究','知识图谱','机器人','无人系统','创意征集','文献翻译','医学','政策法规','其他'];

const STATUS_COLOR = { open:'#108a00', in_progress:'#9a6700', completed:'#0969da', closed:'#6b7280' };
const TASK_TEXT = {
  en: {
    domains: {
      全部: 'All',
      软件工程: 'Software engineering',
      人工智能: 'Artificial intelligence',
      计算机学: 'Computer science',
      电子信息: 'Electronic information',
      通信工程: 'Communication engineering',
      大数据: 'Big data',
      理论研究: 'Theoretical research',
      知识图谱: 'Knowledge graph',
      机器人: 'Robotics',
      无人系统: 'Unmanned systems',
      创意征集: 'Creative call',
      文献翻译: 'Academic translation',
      医学: 'Medical',
      政策法规: 'Policy and regulation',
      其他: 'Other',
    },
    statuses: [
      { val: 'all', label: 'All' },
      { val: 'open', label: 'Open submissions' },
      { val: 'in_progress', label: 'Contracting' },
      { val: 'completed', label: 'Completed' },
      { val: 'closed', label: 'Closed' },
    ],
    statusLabels: {
      open: 'Open for submissions',
      in_progress: 'Contracting',
      completed: 'Completed',
      closed: 'Closed',
    },
    sorts: [
      { val: 'latest', label: 'Best match' },
      { val: 'hot', label: 'Most popular' },
      { val: 'prize', label: 'Highest budget' },
    ],
    heroTitle: 'Find delivery-ready requirements',
    heroSubtitle: 'Review real requests, compare budget, status, and delivery requirements, then submit the right proposal.',
    create: 'Post requirement',
    searchPlaceholder: total => `Search requirement name, ID, or description (${total} total)`,
    search: 'Search',
    filters: 'Filters',
    filterAria: 'Filter requirements',
    domain: 'Domain',
    status: 'Status',
    allDomains: 'All domains',
    results: total => `${total} requirements`,
    loading: 'Loading...',
    emptyTitle: 'No matching requirements',
    emptyText: 'Try another filter, or post a new requirement.',
    previous: 'Previous',
    next: 'Next',
    page: (page, pages) => `Page ${page} of ${pages}`,
    statsTitle: 'Requirement data',
    allTasks: 'All requirements',
    publishers: 'Publishers',
    prizePool: 'Prize pool',
    paid: 'Paid',
    adviceTitle: 'Proposal tips',
    adviceText: 'Prioritize requirements with clear scope, budget, and milestones. In your proposal, explain relevant experience, delivery plan, and expected timing.',
    latest: 'Latest posts',
    recentPost: 'Recently posted',
    fixedReward: 'Fixed reward',
    submissions: count => `${count} submissions`,
    views: count => `${count} views`,
    daysLeft: days => `${days} days left`,
    budget: 'Budget',
    details: 'View details',
    month: month => `${month}`,
  },
  zh: {
    domains: {
      全部: '全部',
      软件工程: '软件工程',
      人工智能: '人工智能',
      计算机学: '计算机学',
      电子信息: '电子信息',
      通信工程: '通信工程',
      大数据: '大数据',
      理论研究: '理论研究',
      知识图谱: '知识图谱',
      机器人: '机器人',
      无人系统: '无人系统',
      创意征集: '创意征集',
      文献翻译: '文献翻译',
      医学: '医学',
      政策法规: '政策法规',
      其他: '其他',
    },
    statuses: [
      { val: 'all', label: '全部' },
      { val: 'open', label: '成果征集' },
      { val: 'in_progress', label: '协议签订' },
      { val: 'completed', label: '已完成' },
      { val: 'closed', label: '已关闭' },
    ],
    statusLabels: {
      open: '成果征集中',
      in_progress: '协议签订中',
      completed: '已完成',
      closed: '已关闭',
    },
    sorts: [
      { val: 'latest', label: '综合排序' },
      { val: 'hot', label: '最热' },
      { val: 'prize', label: '奖金最高' },
    ],
    heroTitle: '找到适合交付的需求',
    heroSubtitle: '浏览真实需求，比较预算、状态和交付要求，选择合适的需求提交方案。',
    create: '发布需求',
    searchPlaceholder: total => `搜索需求名称、编号或描述（共 ${total} 个）`,
    search: '搜索',
    filters: '筛选',
    filterAria: '筛选需求',
    domain: '领域',
    status: '状态',
    allDomains: '全部领域',
    results: total => `${total} 个需求`,
    loading: '加载中...',
    emptyTitle: '暂无相关需求',
    emptyText: '换一个筛选条件，或发布一个新的需求。',
    previous: '上一页',
    next: '下一页',
    page: (page, pages) => `第 ${page} 页 / 共 ${pages} 页`,
    statsTitle: '需求数据',
    allTasks: '全部需求',
    publishers: '发布者',
    prizePool: '奖金池',
    paid: '已支付',
    adviceTitle: '接单建议',
    adviceText: '优先选择范围清晰、预算明确、交付节点具体的需求。提交方案时说明相关经验、交付计划和预计时间。',
    latest: '最新发布',
    recentPost: '最近发布',
    fixedReward: '固定悬赏',
    submissions: count => `${count} 份成果`,
    views: count => `${count} 次浏览`,
    daysLeft: days => `剩余 ${days} 天`,
    budget: '预算',
    details: '查看详情',
    month: month => `${month}月`,
  },
};

function daysLeft(deadline) {
  if (!deadline) return null;
  const d = Math.ceil((new Date(deadline) - Date.now()) / 86400000);
  return d > 0 ? d : 0;
}

function fmt(n, language = 'zh') {
  if (n >= 10000) return language === 'zh' ? (n/10000).toFixed(1) + 'w' : (n/1000).toFixed(1) + 'k';
  return n?.toLocaleString() || '0';
}

function shortDate(date, language, text) {
  if (!date) return text.recentPost;
  return new Date(date).toLocaleDateString(language === 'zh' ? 'zh-CN' : 'en-US', { month: 'short', day: 'numeric' });
}

function localizeRewardMode(mode, text, language) {
  if (!mode) return text.fixedReward;
  if (language === 'zh') return mode;
  const normalized = String(mode).trim();
  if (normalized.includes('固定')) return 'Fixed reward';
  if (normalized.includes('悬赏')) return 'Reward';
  if (normalized.includes('协议')) return 'Contract based';
  return normalized;
}

export default function TaskList() {
  const { language, translatePhrase } = useLanguage();
  const text = TASK_TEXT[language] || TASK_TEXT.en;
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total:0, users:0, pool:0, paid:0, domains:0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [kw, setKw] = useState(searchParams.get('keyword') || '');

  const domain = searchParams.get('category') || 'all';
  const status = searchParams.get('status')   || 'all';
  const sort   = searchParams.get('sort')     || 'latest';
  const statusText = text.statuses.find(s => s.val === status)?.label || text.statuses[0].label;
  const sortText = text.sorts.find(s => s.val === sort)?.label || text.sorts[0].label;
  const domainText = domain === 'all' ? text.allDomains : text.domains[domain] || domain;

  const setP = (k, v) => {
    const p = new URLSearchParams(searchParams);
    p.set(k, v); setSearchParams(p); setPage(1);
  };

  const fetchTasks = useCallback(() => {
    setLoading(true);
    api.get('/tasks', { params: { category: domain, status, sort, page, limit: 12, keyword: searchParams.get('keyword') || '' } })
      .then(r => { setTasks(r.data.tasks); setTotal(r.data.total); })
      .finally(() => setLoading(false));
  }, [domain, status, sort, page, searchParams]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);
  useEffect(() => { api.get('/tasks/stats').then(r => setStats(r.data)); }, []);

  const handleSearch = e => { e.preventDefault(); setP('keyword', kw); };

  return (
    <div className="task-list-page">
      <div className="container tl-shell">
        <main className="tl-feed">
          <section className="tl-hero">
            <div>
              <h1>{text.heroTitle}</h1>
              <p>{text.heroSubtitle}</p>
            </div>
            <Link to="/tasks/create" className="tl-create-btn">{text.create}</Link>
          </section>

          <form className="tl-search-bar" onSubmit={handleSearch}>
            <input value={kw} onChange={e => setKw(e.target.value)} placeholder={text.searchPlaceholder(total)} />
            <button type="submit">{text.search}</button>
          </form>

          <section className="tl-top-filters" aria-label={text.filterAria}>
            <strong>{text.filters}</strong>
            <label>
              <span>{text.domain}</span>
              <select value={domain} onChange={e => setP('category', e.target.value)}>
                {DOMAINS.map(d => (
                  <option key={d} value={d === '全部' ? 'all' : d}>{d === '全部' ? text.allDomains : text.domains[d]}</option>
                ))}
              </select>
            </label>
            <label>
              <span>{text.status}</span>
              <select value={status} onChange={e => setP('status', e.target.value)}>
                {text.statuses.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
              </select>
            </label>
          </section>

          <div className="tl-tabs">
            {text.sorts.map(s => (
              <button key={s.val} className={sort === s.val ? 'active' : ''} onClick={() => setP('sort', s.val)}>{s.label}</button>
            ))}
          </div>

          <div className="tl-results-note">
            <strong>{text.results(total)}</strong>
            <span>{domainText} · {statusText} · {sortText}</span>
          </div>

          {loading ? <div className="tl-loading">{text.loading}</div> : (
            <>
              <section className="tl-grid">
                {tasks.length === 0 ? (
                  <div className="tl-empty">
                    <h3>{text.emptyTitle}</h3>
                    <p>{text.emptyText}</p>
                    <Link to="/tasks/create" className="tl-create-btn">{text.create}</Link>
                  </div>
                ) : tasks.map(t => <TaskCard key={t.id} task={t} language={language} text={text} translatePhrase={translatePhrase} />)}
              </section>
              {total > 12 && (
                <div className="tl-pagination">
                  <button disabled={page===1} onClick={() => setPage(p=>p-1)}>{text.previous}</button>
                  <span>{text.page(page, Math.ceil(total/12))}</span>
                  <button disabled={page*12>=total} onClick={() => setPage(p=>p+1)}>{text.next}</button>
                </div>
              )}
            </>
          )}
        </main>

        <aside className="tl-sidebar">
          <div className="tl-side-card">
            <h3>{text.statsTitle}</h3>
            <div className="tl-stat-row"><span>{text.allTasks}</span><strong>{fmt(stats.total, language)}</strong></div>
            <div className="tl-stat-row"><span>{text.publishers}</span><strong>{fmt(stats.users, language)}</strong></div>
            <div className="tl-stat-row"><span>{text.prizePool}</span><strong>¥{fmt(stats.pool, language)}</strong></div>
            <div className="tl-stat-row"><span>{text.paid}</span><strong>¥{fmt(stats.paid, language)}</strong></div>
          </div>

          <div className="tl-side-card">
            <h3>{text.adviceTitle}</h3>
            <p>{text.adviceText}</p>
          </div>

          <LatestSidebar text={text} translatePhrase={translatePhrase} />
        </aside>
      </div>
    </div>
  );
}

function TaskCard({ task, language, text, translatePhrase }) {
  const days = daysLeft(task.deadline);
  return (
    <Link to={`/tasks/${task.id}`} className="task-card-v2">
      <div className="tc2-top">
        <div>
          <span className="tc2-status" style={{ background: STATUS_COLOR[task.status]+'18', color: STATUS_COLOR[task.status] }}>
            {text.statusLabels[task.status] || task.status}
          </span>
          <span className="tc2-posted">{shortDate(task.created_at, language, text)}</span>
        </div>
        <span className="tc2-save">♡</span>
      </div>
      <div className="tc2-main">
        <h3 className="tc2-title">{translatePhrase(task.title)}</h3>
        <p className="tc2-desc">{translatePhrase(task.description)}</p>
        <div className="tc2-meta">
          <span>{text.domains[task.category] || translatePhrase(task.category)}</span>
          <span>{localizeRewardMode(task.reward_mode, text, language)}</span>
          <span>{text.submissions(task.submit_count || 0)}</span>
          <span>{text.views(task.view_count || 0)}</span>
          {days !== null && <span>{text.daysLeft(days)}</span>}
        </div>
      </div>
      <div className="tc2-bottom">
        <div>
          <strong>¥{fmt(task.budget_max, language)}</strong>
          <span>{text.budget}</span>
        </div>
        <span>{text.details}</span>
      </div>
    </Link>
  );
}

function LatestSidebar({ text, translatePhrase }) {
  const [latest, setLatest] = useState([]);
  useEffect(() => {
    api.get('/tasks?limit=8&sort=latest').then(r => setLatest(r.data.tasks));
  }, []);
  return (
    <div className="tl-side-card latest-card">
      <div className="latest-title">{text.latest}</div>
      {latest.map(t => {
        const d = t.created_at ? new Date(t.created_at) : new Date();
        return (
          <Link key={t.id} to={`/tasks/${t.id}`} className="latest-item">
            <div className="latest-date">
              <div className="latest-day">{d.getDate()}</div>
              <div className="latest-month">{text.month(d.getMonth()+1)}</div>
            </div>
            <div className="latest-body">
              <div className="latest-item-title">{translatePhrase(t.title)}</div>
              {t.status === 'open' && <span className="new-badge">NEW</span>}
            </div>
          </Link>
        );
      })}
    </div>
  );
}
